import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Statistic,
  message,
  Spin,
  Modal,
  Form,
  Input,
  Space
} from "antd";
import {
  DollarOutlined,
  MailOutlined,
  DownloadOutlined,
  CloseOutlined
} from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import AdminBreadCrumb from "components/AdminBreadCrumb";
import facturaApi from "../../facturas/store/api";
import PayAllInvoicesButton from "components/PayAllInvoicesButton/PayAllInvoicesButton";
import "./ReceivablesReport.scss";

const ReceivablesReport = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [clientsData, setClientsData] = useState([]);
  const [totalDue, setTotalDue] = useState(0);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [ccAccounting, setCcAccounting] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [emailingClient, setEmailingClient] = useState(false);
  const [clientFacturasMap, setClientFacturasMap] = useState({});

  const breadCrumbs = [
    { text: "BILLING", url: "/admin/billing-dashboard" },
    { text: "REPORTS", url: "/admin/billing/reports" },
    { text: "RECEIVABLES", url: "/admin/billing/reports/receivables" }
  ];

  useEffect(() => {
    loadReceivablesData();
  }, []);

  const loadReceivablesData = async () => {
    setLoading(true);
    try {
      // Fetch all unpaid, partial, and overdue facturas
      const response = await facturaApi.getFacturas({
        pageSize: 10000,
        status: "unpaid,partial,overdue"
      });

      const facturas = response.data || [];

      // Group by client
      const clientsMap = {};
      const facturasMap = {};

      facturas.forEach((factura) => {
        const clientId = factura.clientId;
        // Handle both cases: client as string or client as object
        const clientName =
          typeof factura.client === "string"
            ? factura.client
            : factura.client?.clientName || "Unknown Client";
        const accountingEmail =
          typeof factura.client === "object"
            ? factura.client?.accountingEmail
            : factura.accountingEmail;
        const status = factura.status || "unpaid";
        const total = parseFloat(factura.total) || 0;
        const paidAmount = parseFloat(factura.paidAmount) || 0;
        const balance = total - paidAmount;

        // Store facturas by clientId for PayAllInvoicesButton
        if (!facturasMap[clientId]) {
          facturasMap[clientId] = [];
        }
        facturasMap[clientId].push(factura);

        if (!clientsMap[clientId]) {
          clientsMap[clientId] = {
            clientId,
            clientName,
            accountingEmail: accountingEmail || null,
            unpaidCount: 0,
            unpaidTotal: 0,
            partialCount: 0,
            partialBalance: 0,
            overdueCount: 0,
            overdueTotal: 0,
            totalDue: 0
          };
        }

        if (status === "unpaid") {
          clientsMap[clientId].unpaidCount++;
          clientsMap[clientId].unpaidTotal += total;
        } else if (status === "partial") {
          clientsMap[clientId].partialCount++;
          clientsMap[clientId].partialBalance += balance;
        } else if (status === "overdue") {
          clientsMap[clientId].overdueCount++;
          clientsMap[clientId].overdueTotal += total;
        }
      });

      // Calculate total due for each client
      const clientsArray = Object.values(clientsMap).map((client) => {
        client.totalDue =
          client.unpaidTotal + client.partialBalance + client.overdueTotal;
        return client;
      });

      // Sort by total due descending
      clientsArray.sort((a, b) => b.totalDue - a.totalDue);

      // Calculate grand total
      const grandTotal = clientsArray.reduce((sum, client) => sum + client.totalDue, 0);

      setClientsData(clientsArray);
      setTotalDue(grandTotal);
      setClientFacturasMap(facturasMap);
    } catch (error) {
      console.error("Error loading receivables data:", error);
      message.error("Failed to load receivables data");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailClient = (client, e) => {
    e.stopPropagation(); // Prevent row click

    if (!client.accountingEmail) {
      message.error("No accounting email found for this client");
      return;
    }

    // Build default email list
    const defaultEmails = [client.accountingEmail];
    setSelectedEmails(defaultEmails);
    setSelectedClient(client);
    setShowEmailModal(true);
  };

  const handleAddEmail = () => {
    if (!newEmail.trim()) {
      message.error("Please enter a valid email address");
      return;
    }

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail.trim())) {
      message.error("Please enter a valid email address");
      return;
    }

    if (selectedEmails.includes(newEmail.trim())) {
      message.error("Email already added");
      return;
    }

    setSelectedEmails([...selectedEmails, newEmail.trim()]);
    setNewEmail("");
  };

  const handleRemoveEmail = (emailToRemove) => {
    setSelectedEmails(selectedEmails.filter((email) => email !== emailToRemove));
  };

  const handleSendEmail = async () => {
    if (selectedEmails.length === 0) {
      message.error("Please select at least one email address");
      return;
    }

    setEmailingClient(true);
    try {
      // Get all unpaid/partial/overdue facturas for this client
      const response = await facturaApi.getFacturas({
        clientId: selectedClient.clientId,
        status: "unpaid,partial,overdue",
        pageSize: 1000
      });

      const clientFacturas = response.data || [];
      const facturaIds = clientFacturas.map((f) => f.id);

      // Use the existing email factura API for each factura
      // This will send individual emails for each factura
      await Promise.all(
        facturaIds.map((facturaId) =>
          facturaApi.emailFactura(facturaId, {
            to: selectedEmails,
            ccAccounting: ccAccounting
          })
        )
      );

      message.success(`Receivables summary sent to ${selectedClient.clientName}`);
      setShowEmailModal(false);
      setSelectedEmails([]);
      setCcAccounting(true);
      setNewEmail("");
      setSelectedClient(null);
    } catch (error) {
      console.error("Error sending email:", error);
      message.error(error.response?.data?.message || "Failed to send email");
    } finally {
      setEmailingClient(false);
    }
  };

  const handleEmailModalCancel = () => {
    setShowEmailModal(false);
    setSelectedEmails([]);
    setCcAccounting(true);
    setNewEmail("");
    setSelectedClient(null);
  };

  const handleRowClick = (client, event) => {
    // Don't navigate if clicking on a button or action element
    if (
      event.target.closest("button") ||
      event.target.closest(".ant-btn") ||
      event.target.closest(".ant-modal") ||
      event.target.closest(".ant-modal-mask")
    ) {
      return;
    }
    // Navigate to facturas page with client filter
    history.push(
      `/admin/facturas?clientId=${client.clientId}&status=unpaid,partial,overdue`
    );
  };

  const handlePayAllSuccess = (clientId) => {
    // Refresh the report after successful payment
    loadReceivablesData();
  };

  const handleExportCSV = () => {
    // Create CSV content
    const headers = [
      "Client Name",
      "Unpaid Count",
      "Unpaid Total",
      "Partial Count",
      "Partial Balance",
      "Overdue Count",
      "Overdue Total",
      "Total Due"
    ];

    const rows = clientsData.map((client) => [
      client.clientName,
      client.unpaidCount,
      client.unpaidTotal.toFixed(2),
      client.partialCount,
      client.partialBalance.toFixed(2),
      client.overdueCount,
      client.overdueTotal.toFixed(2),
      client.totalDue.toFixed(2)
    ]);

    // Add total row
    rows.push(["TOTAL", "", "", "", "", "", "", totalDue.toFixed(2)]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(","))
    ].join("\n");

    // Download CSV
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `receivables-report-${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    {
      title: "Client Name",
      dataIndex: "clientName",
      key: "clientName",
      width: 200,
      fixed: "left",
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>
    },
    {
      title: "Unpaid",
      key: "unpaid",
      width: 150,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {record.unpaidCount} invoice{record.unpaidCount !== 1 ? "s" : ""}
          </div>
          <div style={{ color: "#1890ff" }}>${record.unpaidTotal.toFixed(2)}</div>
        </div>
      )
    },
    {
      title: "Partial",
      key: "partial",
      width: 150,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {record.partialCount} invoice{record.partialCount !== 1 ? "s" : ""}
          </div>
          <div style={{ color: "#faad14" }}>${record.partialBalance.toFixed(2)}</div>
        </div>
      )
    },
    {
      title: "Overdue",
      key: "overdue",
      width: 150,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {record.overdueCount} invoice{record.overdueCount !== 1 ? "s" : ""}
          </div>
          <div style={{ color: "#cf1322" }}>${record.overdueTotal.toFixed(2)}</div>
        </div>
      )
    },
    {
      title: "Total Due",
      dataIndex: "totalDue",
      key: "totalDue",
      width: 120,
      render: (value) => (
        <span style={{ fontWeight: 600, fontSize: 16, color: "#000000" }}>
          ${value.toFixed(2)}
        </span>
      )
    },
    {
      title: "Action",
      key: "action",
      width: 250,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<MailOutlined />}
            onClick={(e) => handleEmailClient(record, e)}
            disabled={!record.accountingEmail}
          >
            Email
          </Button>
          <PayAllInvoicesButton
            facturas={clientFacturasMap[record.clientId] || []}
            onSuccess={() => handlePayAllSuccess(record.clientId)}
            size="small"
            isClientSide={false}
            clientName={record.clientName}
            skipConfirm={true}
          />
        </Space>
      )
    }
  ];

  return (
    <div className="receivables-report-page admin">
      <div className="common-header">
        <AdminBreadCrumb breadCrumbs={breadCrumbs} />
      </div>

      <div className="content-wrapper">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 24
          }}
        >
          <div>
            <h2>Receivables Report</h2>
            <p style={{ marginBottom: 0, color: "#666" }}>
              Outstanding invoices and payment tracking
            </p>
          </div>
          <Button
            type="default"
            icon={<DownloadOutlined />}
            onClick={handleExportCSV}
            disabled={loading || clientsData.length === 0}
          >
            Export CSV
          </Button>
        </div>

        <Card style={{ marginBottom: 24 }}>
          <Statistic
            title="Total Due"
            value={totalDue}
            precision={2}
            prefix={<DollarOutlined />}
            valueStyle={{ color: "#cf1322", fontSize: 32, fontWeight: 600 }}
          />
          <div style={{ marginTop: 8, fontSize: 14, color: "#666" }}>
            Includes all Unpaid, Partial, and Overdue invoices
          </div>
        </Card>

        {loading ? (
          <Card>
            <div style={{ textAlign: "center", padding: "2rem" }}>
              <Spin size="large" />
              <div style={{ marginTop: 16, color: "#666" }}>
                Loading receivables data...
              </div>
            </div>
          </Card>
        ) : (
          <Card>
            <Table
              dataSource={clientsData}
              columns={columns}
              rowKey="clientId"
              pagination={{
                pageSize: 20,
                showTotal: (total) => `Total ${total} clients`
              }}
              scroll={{ x: 1050 }}
              onRow={(record) => ({
                onClick: (event) => handleRowClick(record, event),
                style: { cursor: "pointer" }
              })}
              rowClassName="clickable-row"
            />
          </Card>
        )}

        {/* Email Client Modal */}
        <Modal
          title={`Email Receivables Summary to ${selectedClient?.clientName || "Client"}`}
          open={showEmailModal}
          onCancel={handleEmailModalCancel}
          width={600}
          footer={null}
        >
          <Form layout="vertical">
            <Form.Item label="Email Recipients">
              <div style={{ marginBottom: 16 }}>
                {selectedEmails.map((email, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "8px 12px",
                      marginBottom: 8,
                      backgroundColor: "#f5f5f5",
                      borderRadius: 4,
                      border: "1px solid #d9d9d9"
                    }}
                  >
                    <span>{email}</span>
                    <Button
                      type="text"
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={() => handleRemoveEmail(email)}
                      style={{ color: "#ff4d4f" }}
                    />
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <Input
                  placeholder="Enter additional email address"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onPressEnter={handleAddEmail}
                  style={{ flex: 1 }}
                />
                <Button onClick={handleAddEmail}>Add</Button>
              </div>
            </Form.Item>

            <Form.Item>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={ccAccounting}
                  onChange={(e) => setCcAccounting(e.target.checked)}
                  id="ccAccounting"
                />
                <label htmlFor="ccAccounting">CC accounting@goflexio.com</label>
              </div>
            </Form.Item>

            <div
              style={{
                padding: "12px",
                backgroundColor: "#e6f7ff",
                borderRadius: 4,
                marginBottom: 16
              }}
            >
              <p style={{ margin: 0, fontSize: 13, color: "#666" }}>
                This will send individual invoice emails for all unpaid, partial, and
                overdue facturas for this client.
              </p>
            </div>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  onClick={handleSendEmail}
                  loading={emailingClient}
                  disabled={selectedEmails.length === 0}
                >
                  {emailingClient ? "Sending..." : "Send Emails"}
                </Button>
                <Button onClick={handleEmailModalCancel}>Cancel</Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default ReceivablesReport;
